import React from "react";

import { faPencil } from "@fortawesome/pro-solid-svg-icons";

import { faCircleCheck } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonStandard, Text } from "@performyard/py-components";

import { ChatErrorBanner } from "./ChatErrorBanner";
import GeneratedContentContainer from "./GeneratedContentContainer";

import { resolveCompetencies, useCompetencyCreation } from "../hooks/useCompetencyCreation";

import { useAskPerformYardStore } from "../AskPerformYardStore";

const COMPETENCIES_ADMIN_PATH = "/administration/competencies";

const CompetencyPreviewCard: React.FC<{
	name: string;
	description: string;
	levelNames: string[];
}> = ({ name, description, levelNames }) => (
	<div className="box-border flex min-w-0 max-w-full flex-col gap-2 rounded-md border border-solid border-neutral-3 bg-white p-3">
		<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
			{name}
		</Text>
		<Text size="caption" color="neutral-7" dangerouslySetClassName="line-clamp-2">
			{description}
		</Text>
		<div className="flex flex-wrap gap-1">
			{levelNames.map((levelName) => (
				<span
					key={levelName}
					className="rounded-full bg-primary-1 px-2 py-0.5 text-xs font-medium text-primary-7"
				>
					{levelName}
				</span>
			))}
		</div>
	</div>
);

export const CompetencyFrameworkCard: React.FC<{ messageId: string }> = ({ messageId }) => {
	const framework = useAskPerformYardStore((s) => s.competencyFramework);
	const openCompetencyEditPanel = useAskPerformYardStore((s) => s.openCompetencyEditPanel);
	const isExpanded = useAskPerformYardStore((s) => s.isExpanded);
	const toggleExpand = useAskPerformYardStore((s) => s.toggleExpand);
	const { saveFramework, isSaving } = useCompetencyCreation();

	if (!framework || framework.messageId !== messageId) return null;
	const { suggestions, editedFields, status, errorMessage } = framework;

	const resolvedCompetencies = resolveCompetencies(suggestions, editedFields);

	const handleEdit = () => {
		if (!isExpanded) toggleExpand();
		openCompetencyEditPanel();
	};

	return (
		<GeneratedContentContainer
			title={`Competency Framework (${suggestions.length} competencies)`}
			headerAction={{
				label: "Edit",
				iconLeft: faPencil,
				onClick: handleEdit,
				disabled: status === "saved",
			}}
		>
			<div className="flex flex-col gap-2 py-1">
				{suggestions.map((s) => {
					const levels = editedFields[s.id]?.levels ?? s.levels;
					return (
						<CompetencyPreviewCard
							key={s.id}
							name={editedFields[s.id]?.name ?? s.name}
							description={editedFields[s.id]?.description ?? s.description}
							levelNames={levels.map((l) => l.name)}
						/>
					);
				})}
			</div>

			{status === "error" && errorMessage && <ChatErrorBanner type="error" message={errorMessage} />}

			<div className="flex justify-end pt-2">
				{status === "saved" ? (
					<ButtonStandard
						variant="text"
						color="green"
						size="medium"
						iconLeft={faCircleCheck}
						onClick={() => window.open(COMPETENCIES_ADMIN_PATH, "_blank")}
						dangerouslySetClassName="!w-auto"
					>
						View Competencies
					</ButtonStandard>
				) : (
					<ButtonStandard
						variant="solid"
						color="primary"
						size="medium"
						onClick={() => saveFramework(resolvedCompetencies)}
						disabled={isSaving}
						dangerouslySetClassName="w-fit"
					>
						{isSaving ? "Saving..." : "Approve & Save"}
					</ButtonStandard>
				)}
			</div>
		</GeneratedContentContainer>
	);
};
